import { useContext } from 'react';
import { IconArrowBack, IconX } from '@tabler/icons-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useMutateCreateProject } from '@/reactQuery/post';
import { LangContext } from '@/context/LangProvider';
import { showToastErrorMessage } from '@/utils/showToastErrorMessage';
import ErrorMessage from '../error-message/ErrorMessage';
import { MODAL_TOAST_CONTAINER_ID } from '../ui/ModalToastContainer';

interface CreateFromScratchProps {
  goBack: () => void;
  closeModalAndRemoveData: () => void;
}

const defaultValues = {
  title: '',
};

interface FormValues {
  title: string;
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required('msg.projectTitleIsRequired'),
});

function CreateFromScratch({ goBack, closeModalAndRemoveData }: CreateFromScratchProps) {
  const { lang } = useContext(LangContext);
  const { workspace_id: workspaceId, organization_id: organizationId } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const { mutateAsync } = useMutateCreateProject(organizationId, workspaceId);

  const onSubmit: SubmitHandler<FormValues> = async ({ title }: FormValues) => {
    try {
      await mutateAsync({ title: title.trim(), workspaceId });
      closeModalAndRemoveData();
      reset();
      toast.success(`${title} ${lang.get('msg.successfullyCreated')}`);
    } catch (err) {
      showToastErrorMessage(err, { containerId: MODAL_TOAST_CONTAINER_ID });
    }
  };

  const projectNameError = errors?.title?.message;

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden overflow-y-auto">
      <div className="fixed top-0 left-0 right-0 flex items-center justify-between w-full pr-12 bg-white pl-7 h-14">
        {/* go back */}
        <button type="button" onClick={goBack}>
          <IconArrowBack size={24} />
        </button>
        {/* close modal */}
        <button type="button" onClick={closeModalAndRemoveData}>
          <IconX size={24} />
        </button>
      </div>
      <div className="flex flex-col items-center justify-center w-full h-full text-center">
        <h2 className="text-3xl font-bold">{lang.get('msg.enterProjectTitle')}</h2>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col items-start justify-center mt-10 w-fit md900:w-full gap-1"
        >
          <div className="relative flex items-center w-full">
            <Input
              {...register('title')}
              placeholder="My new Tellet"
              hasError={!!projectNameError}
              className="w-full  w-[30rem] md900:w-full mx-auto rounded-r-none"
            />
            <Button variant="default" disabled={isSubmitting} className="capitalize rounded-l-none">
              {lang.get('msg.createProject')}
            </Button>
          </div>
          {projectNameError && <ErrorMessage message={projectNameError} />}
        </form>
      </div>
    </div>
  );
}

export default CreateFromScratch;
